<template>
  <InfuseModal :title="t('title')" @close="emit('close')">
    <InfuseNote
      v-if="overrideStatus && overrideStatus !== StatusOverride.OUTOFORDER && overrideStatus !== StatusOverride.UNKNOWN"
      class="max-w-[650px]"
      type="warn"
    >
      <!-- It is possible to call the API to set a status other than OUTOFORDER / UNKNOWN. If we detect that this has
           happened, show an additional message to the user to let them understand they might need to think twice. -->
      <span v-html="t(`help.${connectorId ? 'conn' : 'cs'}.otherStatusOverrideActive`, { status: overrideStatus })" />
    </InfuseNote>

    <InfuseNote class="max-w-[650px]">
      <span
        v-html="
          t(
            `help.${connectorId ? 'conn' : 'cs'}.${
              overrideStatus === StatusOverride.OUTOFORDER ? 'outOfOrderResolved' : 'outOfOrder'
            }`,
            { connectorId }
          )
        "
      />
    </InfuseNote>

    <template #actions>
      <InfuseButton data-testid="modal-cancel-button" color="secondary" type="button" @click="emit('close')">
        {{ t('action.cancel') }}
      </InfuseButton>

      <InfuseButton
        data-testid="modal-confirm-button"
        color="primary"
        type="button"
        @click="emit('toggleOutOfOrder', connectorId)"
      >
        {{ t(`action.toggleOutOfOrder.${overrideStatus === StatusOverride.OUTOFORDER ? 'off' : 'on'}`) }}
      </InfuseButton>
    </template>
  </InfuseModal>
</template>

<script setup lang="ts">
import { StatusOverride } from '@infuse/cpms/charging-station/store';
import { InfuseButton, InfuseModal, InfuseNote } from '@infuse/ui/components';
import { useI18n } from 'vue-i18n';

interface Props {
  connectorId?: number;
  overrideStatus?: StatusOverride;
}

interface Emits {
  (event: 'close'): void;
  (event: 'toggleOutOfOrder', connectorId?: number): void;
}

defineProps<Props>();
const emit = defineEmits<Emits>();

const { t } = useI18n();
</script>

<i18n>
{
  "en": {
    "title": "Out of order",
    "action": {
      "cancel": "Cancel",
      "toggleOutOfOrder": {
        "off": "Remove out of order",
        "on": "Set out of order"
      }
    },
    "help": {
      "conn": {
        "otherStatusOverrideActive": "Another status override '{status}' is currently active on this connector. This status will be overridden, so please make sure that this is intended.",
        "outOfOrder": "This connector will be set out of order. This means that status changes communicated by the charging station for this connector will be ignored while this setting is active. Instead, this 'Out of order' status will be communicated with roaming partners (OCPI).",
        "outOfOrderResolved": "This connector will no longer be set as being out of order. Status changes communicated by the charging station for this connector will once again be processed. It may take a few moments for the charging station to communicate its actual status. Requesting a message of type Status notification for this connector will make the charging station report its latest status."
      },
      "cs": {
        "otherStatusOverrideActive": "Another status override '{status}' is currently active on this charging station. This status will be overridden, so please make sure that this is intended.",
        "outOfOrder": "This charging station will be set out of order. This means that status changes communicated by the charging station will be ignored while this setting is active. Instead, this 'Out of order' status will be communicated with roaming partners (OCPI).",
        "outOfOrderResolved": "This charging station no longer be set as being out of order. Status changes communicated by the charging station will once again be processed. It may take a few moments for the charging station to communicate its actual status. Requesting a message of type Status notification will make the charging station report its latest status."
      }
    }
  },
  "nl": {
    "title": "Buiten dienst",
    "action": {
      "cancel": "Annuleren",
      "toggleOutOfOrder": {
        "off": "Zet terug in dienst",
        "on": "Zet buiten dienst"
      }
    },
    "help": {
      "conn": {
        "otherStatusOverrideActive": "Er is op deze aansluiting reeds een statusoverschrijving '{status}' actief. Deze zal nu worden overschreven, dus verifeer of dit gewenst is.",
        "outOfOrder": "Deze aansluiting zal buiten dienst gezet worden. Dit betekent dat statusveranderingen die de laadpaal voor deze aansluiting doorstuurt genegeerd zullen worden zolang deze instelling actief is. In plaats daarvan zal deze 'Buiten dienst' status worden doorgegeven aan roaming partners (OCPI).",
        "outOfOrderResolved": "Deze aansluiting zal terug in dienst gezet worden. Statusveranderingen van deze laadpaal op deze aansluiting zullen weer verwerkt worden. Het kan even duren voordat de laadpaal zijn actuele status doorgeeft. Een berichtzoek voor een Statusnotificatie op deze aansluiting zorgt ervoor dat de laadpaal meteen zijn actuele status doorstuurt."
      },
      "cs": {
        "otherStatusOverrideActive": "Er is op deze laadpaal reeds een statusoverschrijving '{status}' actief. Deze zal nu worden overschreven, dus verifeer of dit gewenst is.",
        "outOfOrder": "Deze laadpaal zal buiten dienst gezet worden. Dit betekent dat statusveranderingen die de laadpaal doorstuurt genegeerd zullen worden zolang deze instelling actief is. In plaats daarvan zal deze 'Buiten dienst' status worden doorgegeven aan roaming partners (OCPI).",
        "outOfOrderResolved": "Deze laadpaal zal terug in dienst gezet worden. Statusveranderingen van deze laadpaal zullen weer verwerkt worden. Het kan even duren voordat de laadpaal zijn actuele status doorgeeft. Een berichtzoek voor een Statusnotificatie zorgt ervoor dat de laadpaal meteen zijn actuele status doorstuurt."
      }
    }
  }
}
</i18n>
